import {
  AnchorPanel,
  AnchorPanelTheme,
  Breakpoint,
  EventTile,
  EventTileDataType,
  EventTileUpcomingData,
  KnfStatus,
  ProgressWheel,
  ProgressWheelVariant,
  TypographyVariant,
  useFormatDate,
  UseFormatDate,
} from '@elearning-platform/ui';
import React, { useCallback, useMemo } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { RouteNameEnum, ViewImage } from '../../../../enums';
import { useAuth, useRedirectTo } from '../../../../hooks';
import { UseAuth, UseRedirectTo } from '../../../../interfaces/hooks';
import { RecommendedEvent, Statistic } from '../../dashboardV2.types';

import {
  BottomContainer,
  ContentContainer,
  DesktopEventCardContainer,
  DesktopKnfStatus,
  HeaderBg,
  HeaderWrapper,
  MobileEventCardContainer,
  MobileEventTile,
  MobileEventTileContainer,
  RightBottomContainer,
  SectionsWrapper,
  StatisticsContainer,
  StyledTypography,
  StyledUserName,
  Wrapper,
} from './header.styled';
import { HeaderProps } from './header.types';

export const Header: React.FC<HeaderProps> = (props: HeaderProps): JSX.Element => {
  const { sections, statistics, upcomingEvent }: HeaderProps = props;
  const { t }: TransProps<never> = useTranslation();
  const { redirect }: UseRedirectTo = useRedirectTo();
  const { getTimestampMilisecValue }: UseFormatDate = useFormatDate();
  const isTablet: boolean = useMediaQuery({
    query: Breakpoint.SmallDesktop,
  });
  const { userData }: UseAuth = useAuth();

  const transformUpcomingEvent: (item: RecommendedEvent) => EventTileUpcomingData
  = useCallback((item: RecommendedEvent): EventTileUpcomingData => ({
    type: EventTileDataType.Upcoming,
    id: item.id,
    name: item.course.name,
    subheader: t('competence.app.dashboard.eventTile.heading'),
    date: {
      from: getTimestampMilisecValue(item.timestampStart),
      to: getTimestampMilisecValue(item.timestampEnd),
    },
    onCardClick: (): void => redirect({ route: RouteNameEnum.TrainingDetails, params: { id: item.id.toString() } }),
    location: item.city,
    img: item.course.image ?? ViewImage.DashboardEventCardBackground,
  }), [t, redirect, getTimestampMilisecValue]);

  const cardData: EventTileUpcomingData | null = useMemo(
    (): EventTileUpcomingData | null => {
      if (!upcomingEvent) {
        return null;
      }

      return transformUpcomingEvent(upcomingEvent);
    },
    [transformUpcomingEvent, upcomingEvent]
  );

  return (
    <>
      <Wrapper>
        <HeaderBg />
        <ContentContainer $withRightPadding={!!cardData || !!userData?.knfInfo}>
          <HeaderWrapper>
            <StyledTypography variant={!isTablet ? TypographyVariant.DisplaySM : TypographyVariant.DisplayLG}>
              {t('competence.app.dashboard.header.welcome', { comma: userData?.name ? ',' : '' })}
            </StyledTypography>
            {!!userData?.name && (
              <StyledUserName variant={!isTablet ? TypographyVariant.DisplaySM : TypographyVariant.DisplayLG}>
                {userData.name}
              </StyledUserName>
            )}
          </HeaderWrapper>
          <BottomContainer>
            <SectionsWrapper>
              <AnchorPanel sections={sections} theme={AnchorPanelTheme.Light} />
            </SectionsWrapper>
            <RightBottomContainer>
              {
                !!statistics && (
                  <StatisticsContainer>
                    {Object.keys(statistics).map((key: string): JSX.Element => {
                      const { label, count, total }: Statistic = statistics[key];

                      return (
                        <ProgressWheel
                          denominatorBase={total ?? 0}
                          key={key}
                          label={label}
                          progress={count}
                          variant={ProgressWheelVariant.Light}
                        />
                      );
                    })}
                  </StatisticsContainer>
                )
              }
              {!!((cardData || userData?.knfInfo) && isTablet) && (
                <DesktopEventCardContainer>
                  {!!userData?.knfInfo && (
                    <DesktopKnfStatus knfNumber={userData.knfInfo.value} />
                  )}
                  {!!cardData && (
                    <EventTile data={cardData} />
                  )}
                </DesktopEventCardContainer>
              )}
            </RightBottomContainer>
          </BottomContainer>
        </ContentContainer>
      </Wrapper>
      {!!((cardData || userData?.knfInfo) && !isTablet) && (
        <MobileEventCardContainer>
          {!!userData?.knfInfo && (
            <KnfStatus knfNumber={userData.knfInfo.value} />
          )}
          {!!cardData && (
            <MobileEventTileContainer>
              <MobileEventTile data={cardData} />
            </MobileEventTileContainer>
          )}
        </MobileEventCardContainer>
      )}
    </>
  );
};
